import { ChevronDown, Heart, HelpCircle, LogOut, Settings, User } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLogout, useClientSession, useMasterSession } from '@/features/auth/hooks/useSession';
import { useClientSignOut } from '@/features/client-hub/hooks/useClientAccount';
import { useShowcaseLink } from '@/features/dashboard/hooks/useShowcaseLink';
import { ROUTES } from '@/shared/config/routes';
import { useClickOutside, useEscapeKey } from '@/shared/hooks';
import { initialsOf } from '@/shared/lib';

export type AppAudience = 'master' | 'client';

type MenuItem = { to: string; icon: typeof User; labelKey: string };

/**
 * Menu entries per role: the two audiences manage different things. The
 * master's public profile is added from the real slug once it is known — it
 * used to link every master to `/master/anna`.
 */
const MENU_ITEMS: Record<AppAudience, MenuItem[]> = {
  master: [
    { to: ROUTES.app.settingsProfile, icon: Settings, labelKey: 'header.settings' },
    { to: ROUTES.support, icon: HelpCircle, labelKey: 'header.help' },
  ],
  client: [
    { to: ROUTES.client.profile, icon: User, labelKey: 'header.editProfile' },
    { to: ROUTES.client.favorites, icon: Heart, labelKey: 'header.favorites' },
    { to: ROUTES.support, icon: HelpCircle, labelKey: 'header.support' },
  ],
};

/**
 * Avatar button with the account dropdown, shared by both dashboards.
 *
 * Everything in it is the signed-in person's own: the name from the session,
 * initials drawn locally (the avatar was a third-party image of a sample name,
 * fetched on every page), and a sign-out that ends the session — it used to
 * navigate home and leave the session open.
 */
export const UserMenu = ({ audience }: { audience: AppAudience }) => {
  const { t } = useTranslation('app');
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useClickOutside(containerRef, () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  const isMaster = audience === 'master';
  const master = useMasterSession();
  const client = useClientSession();
  const showcase = useShowcaseLink(isMaster);
  const signOutMaster = useLogout();
  const signOutClient = useClientSignOut();

  const displayName = (isMaster ? master.user?.name : client.client?.name) ?? '';
  const items: MenuItem[] =
    isMaster && showcase.slug
      ? [
          { to: ROUTES.master(showcase.slug), icon: User, labelKey: 'header.publicProfile' },
          ...MENU_ITEMS.master,
        ]
      : MENU_ITEMS[audience];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={t('header.userMenu')}
        className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-gray-50"
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-white md:h-10 md:w-10 md:text-sm"
        >
          {initialsOf(displayName)}
        </span>
        <div className="mr-1 ml-1 hidden items-center gap-1.5 md:flex">
          {!isMaster && (
            <span className="text-sm font-bold text-gray-700">{displayName.split(' ')[0]}</span>
          )}
          <ChevronDown size={16} className="text-gray-400" />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 z-50 mt-2 w-56 origin-top-right overflow-hidden rounded-2xl border border-gray-100 bg-white py-2 shadow-xl"
          >
            <div className="mb-2 border-b border-gray-100 px-4 py-2">
              <div className="font-bold text-gray-900">{displayName}</div>
              <div className="text-xs text-gray-500">
                {isMaster ? t('header.masterRole') : t('header.clientRole')}
              </div>
            </div>

            {items.map(({ to, icon: Icon, labelKey }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary"
              >
                <Icon size={16} /> {t(labelKey)}
              </Link>
            ))}

            <div className="my-1 border-t border-gray-100" />
            <button
              type="button"
              disabled={signOutMaster.isPending || signOutClient.isPending}
              onClick={() => (isMaster ? signOutMaster : signOutClient).mutate()}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm font-bold text-red-500 transition-colors hover:bg-red-50"
            >
              <LogOut size={16} /> {t('header.logout')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
